// Cliente MQTT único compartido por toda la app (una sola conexión por pestaña).
// Los componentes se suscriben/desuscriben de topics; la conexión no se cierra.
import mqtt from "mqtt";

const MQTT_URL = process.env.REACT_APP_MQTT_URL || "wss://mqtt.netizar.com";

let client = null;
let status = "Connecting...";
const topicHandlers = new Map(); // topic -> Set(handler)
const statusListeners = new Set();
const errorListeners = new Set();

const setStatus = (s) => {
    status = s;
    statusListeners.forEach((cb) => {
        try { cb(s); } catch { }
    });
};

// Soporta comodines MQTT + y #
const topicMatches = (filter, topic) => {
    if (filter === topic) return true;
    const f = filter.split("/");
    const t = topic.split("/");
    for (let i = 0; i < f.length; i++) {
        if (f[i] === "#") return true;
        if (i >= t.length) return false;
        if (f[i] !== "+" && f[i] !== t[i]) return false;
    }
    return f.length === t.length;
};

export const getMqttClient = () => {
    if (client) return client;

    client = mqtt.connect(MQTT_URL, {
        username: process.env.REACT_APP_MQTT_USERNAME,
        password: process.env.REACT_APP_MQTT_PASSWORD,
        clientId: "web_" + Math.random().toString(16).slice(2, 10),
        keepalive: 30,
        reconnectPeriod: 3000,
        connectTimeout: 10000,
        clean: true,
        resubscribe: false, // las re-suscripciones las manejamos nosotros en 'connect'
    });

    client.on("connect", () => {
        setStatus("Connected");
        const topics = [...topicHandlers.keys()];
        if (topics.length) {
            client.subscribe(topics, (err) => {
                if (err) console.error("MQTT subscribe error:", err);
            });
        }
    });

    client.on("message", (topic, message) => {
        topicHandlers.forEach((handlers, filter) => {
            if (topicMatches(filter, topic)) {
                handlers.forEach((h) => {
                    try { h(topic, message); } catch (e) { console.error("MQTT handler error:", e); }
                });
            }
        });
    });

    client.on("reconnect", () => setStatus("Reconnecting..."));
    client.on("offline", () => setStatus("Disconnected"));
    client.on("error", (err) => {
        console.error("MQTT Connection Error:", err);
        setStatus("Error");
        errorListeners.forEach((cb) => {
            try { cb(err); } catch { }
        });
    });

    return client;
};

// Suscribe un handler a un topic. Devuelve la función para desuscribir.
export const subscribeTopic = (topic, handler) => {
    if (!topic) return () => { };
    const c = getMqttClient();

    let handlers = topicHandlers.get(topic);
    if (!handlers) {
        handlers = new Set();
        topicHandlers.set(topic, handlers);
        if (c.connected) {
            c.subscribe(topic, (err) => {
                if (err) console.error(`MQTT subscribe error (${topic}):`, err);
            });
        }
    }
    handlers.add(handler);

    return () => {
        const hs = topicHandlers.get(topic);
        if (!hs) return;
        hs.delete(handler);
        if (hs.size === 0) {
            topicHandlers.delete(topic);
            if (c.connected) {
                c.unsubscribe(topic, (err) => {
                    if (err) console.error(`MQTT unsubscribe error (${topic}):`, err);
                });
            }
        }
    };
};

export const getMqttStatus = () => status;

export const onMqttStatus = (cb) => {
    statusListeners.add(cb);
    cb(status);
    return () => statusListeners.delete(cb);
};

export const onMqttError = (cb) => {
    errorListeners.add(cb);
    return () => errorListeners.delete(cb);
};